import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { useNavigate } from 'react-router-dom';
import { ProgramStatus } from '../../../../../../../../api/types/programs/program-status';
import { StudentProgramsWithCourses } from '../../../../../../../../api/types/students/programs/student-programs-with-courses';
import { ProgramStudentStatus } from '../../../../../../../../api/types/programs/students/program-student-status';
import { observer } from 'mobx-react-lite';
import { getHebrewYear } from '../../../../../../../../utils/hebrew-date-utils';
import { generateStudyStatusPDF } from '../../../../operations/generate-study-status-pdf';
import { generateStudyCertificatePDF } from '../../../../operations/generate-study-certificate-pdf';
import { generateStudyCertificateWord } from '../../../../operations/generate-study-certificate-word';
import { Gender } from '../../../../../../../../api/types/user/user-summary.type';
import { toast } from 'react-toastify';
import './program-header.css';
import { ProgramNavigationDialog } from './components/program-navigation-dialog';
import {
  StudyCertificateDialogConfirmPayload,
  StudyCertificateProgramNameChoice,
  StudyCertificateProgramNameDialog,
} from './components/study-certificate-program-name-dialog';

interface ProgramHeaderProps {
  program: StudentProgramsWithCourses;
  isProgramExpanded: (programId: number) => boolean;
  toggleProgramExpand: (programId: number) => void;
  openStatusDialog: (program: StudentProgramsWithCourses) => void;
  studentName?: string;
  studentId?: string;
  studentGender?: Gender;
}

export const ProgramHeader: React.FC<ProgramHeaderProps> = observer(
  ({
    program,
    isProgramExpanded,
    toggleProgramExpand,
    openStatusDialog,
    studentName,
    studentId,
    studentGender,
  }) => {
    const navigate = useNavigate();
    const [showNavigationDialog, setShowNavigationDialog] = useState(false);
    const [showStudyCertificateNameDialog, setShowStudyCertificateNameDialog] =
      useState(false);
    const getProgramStatusSeverity = (status: ProgramStatus) => {
      switch (status) {
        case ProgramStatus.ACTIVE:
          return 'success';
        case ProgramStatus.INIT:
          return 'warning';
        case ProgramStatus.FINISHED:
          return 'info';
        case ProgramStatus.CLOSE:
          return 'danger';
        default:
          return 'warning';
      }
    };

    const getProgramStatusIcon = (status: ProgramStatus) => {
      switch (status) {
        case ProgramStatus.ACTIVE:
          return 'pi pi-check-circle';
        case ProgramStatus.INIT:
          return 'pi pi-pause';
        case ProgramStatus.FINISHED:
          return 'pi pi-flag-fill';
        case ProgramStatus.CLOSE:
          return 'pi pi-times-circle';
        default:
          return 'pi pi-question';
      }
    };

    const getStudentStatusSeverity = (status: ProgramStudentStatus) => {
      switch (status) {
        case ProgramStudentStatus.STUDYING:
          return 'success';
        case ProgramStudentStatus.REGISTERED:
          return 'info';
        case ProgramStudentStatus.CANCELLED:
          return 'danger';
        case ProgramStudentStatus.LEFT:
          return 'warning';
        case ProgramStudentStatus.COMPLETED:
          return 'success';
        case ProgramStudentStatus.GRADUATED:
          return 'info';
        default:
          return 'warning';
      }
    };

    const getStudentStatusIcon = (status: ProgramStudentStatus) => {
      switch (status) {
        case ProgramStudentStatus.STUDYING:
          return 'pi pi-user';
        case ProgramStudentStatus.REGISTERED:
          return 'pi pi-check-circle';
        case ProgramStudentStatus.CANCELLED:
          return 'pi pi-times-circle';
        case ProgramStudentStatus.LEFT:
          return 'pi pi-sign-out';
        case ProgramStudentStatus.COMPLETED:
          return 'pi pi-check-square';
        case ProgramStudentStatus.GRADUATED:
          return 'pi pi-star';
        default:
          return 'pi pi-question';
      }
    };

    const getStudentStatusText = (
      studentStatus: StudentProgramsWithCourses['studentStatus'],
    ) => {
      switch (studentStatus.status) {
        case ProgramStudentStatus.REGISTERED:
          return `נרשם${studentStatus.registrationDate ? ` - ${new Date(studentStatus.registrationDate).toLocaleDateString('he-IL')}` : ''}`;
        case ProgramStudentStatus.STUDYING:
          return `לומד${studentStatus.studyingStartDate ? ` - ${new Date(studentStatus.studyingStartDate).toLocaleDateString('he-IL')}` : ''}`;
        case ProgramStudentStatus.CANCELLED:
          return `ביטל${studentStatus.cancellationDate ? ` - ${new Date(studentStatus.cancellationDate).toLocaleDateString('he-IL')}` : ''}`;
        case ProgramStudentStatus.LEFT:
          return `עזב${studentStatus.leftDate ? ` - ${new Date(studentStatus.leftDate).toLocaleDateString('he-IL')}` : ''}`;
        case ProgramStudentStatus.COMPLETED:
          return 'סיים';
        case ProgramStudentStatus.GRADUATED:
          return 'דופלם';
        default:
          return 'לא ידוע';
      }
    };

    const getDisplayProgramName = (
      choice: StudyCertificateProgramNameChoice,
    ): string => {
      if (choice === 'additionalProgramName' && program.additionalProgramName) {
        return program.additionalProgramName;
      }
      return program.programName;
    };

    const handleGenerateStudyStatusPDF = async () => {
      try {
        await generateStudyStatusPDF({
          program,
          studentName: studentName || 'שם לא זמין',
          studentId: studentId || 'מספר זהות לא זמין',
        });

        toast.success('קובץ PDF נוצר ורד בהצלחה');
      } catch (error) {
        console.error('Error generating PDF:', error);
        toast.error('שגיאה ביצירת קובץ PDF');
      }
    };

    const handleGenerateStudyCertificate = async (
      payload: StudyCertificateDialogConfirmPayload,
    ) => {
      const certificateData = {
        program,
        studentName: studentName || 'שם לא זמין',
        studentId: studentId || 'מספר זהות לא זמין',
        studentGender,
        displayProgramName: getDisplayProgramName(payload.programNameChoice),
        displayAcademicYear: getHebrewYear(payload.selectedYear),
      };

      try {
        if (payload.format === 'word') {
          await generateStudyCertificateWord(certificateData);
        } else {
          await generateStudyCertificatePDF(certificateData);
        }

        toast.success('אישור לימודים הורד בהצלחה');
      } catch (error) {
        console.error('Error generating study certificate:', error);
        toast.error('שגיאה ביצירת אישור לימודים');
      }
    };

    const handleStudyCertificatePdfButtonClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setShowStudyCertificateNameDialog(true);
    };

    const handleStudyCertificateNameDialogConfirm = (
      payload: StudyCertificateDialogConfirmPayload,
    ) => {
      setShowStudyCertificateNameDialog(false);
      void handleGenerateStudyCertificate(payload);
    };

    const handleNavigateToProgram = () => {
      setShowNavigationDialog(false);
      navigate(`/campuses/${program.branchId}/programs/${program.id}`);
    };

    return (
      <div
        className="program-header"
        onClick={() => toggleProgramExpand(program.id)}
      >
        <div className="program-header-main">
          <div className="header-title">
            {program.programName}
            {program.additionalProgramName &&
              ` - ${program.additionalProgramName}`}
          </div>
          <div className="program-header-right">
            <div className="statuses-section">
              <div
                className="status-section"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <span className="status-label">סטטוס תוכנית</span>
                <Tag
                  value={ProgramStatus.getDisplayText(program.status)}
                  severity={getProgramStatusSeverity(program.status)}
                  /*icon={getProgramStatusIcon(program.status)}*/
                  className="program-status-tag"
                />
              </div>
              <div
                className="status-section"
                onClick={(e) => {
                  e.stopPropagation();
                  openStatusDialog(program);
                }}
              >
                <span className="status-label">סטטוס סטודנט בתוכנית</span>
                <Tag
                  value={getStudentStatusText(program.studentStatus)}
                  severity={getStudentStatusSeverity(
                    program.studentStatus.status,
                  )}
                  icon={getStudentStatusIcon(program.studentStatus.status)}
                  className="program-status-tag"
                />
              </div>
            </div>
            <div className="header-buttons">
              <Button
                icon="pi pi-external-link"
                className="p-button-text p-button-rounded link-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowNavigationDialog(true);
                }}
                aria-label="Navigate to Program"
                tooltip="עבור לדף התוכנית"
                tooltipOptions={{ position: 'bottom' }}
              />
              <Button
                icon="pi pi-file-pdf"
                className="p-button-text p-button-rounded pdf-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleGenerateStudyStatusPDF();
                }}
                aria-label="Generate Study Status PDF"
                tooltip="הורד מצב לימודים"
                tooltipOptions={{ position: 'bottom' }}
              />
              <Button
                icon="pi pi-file-pdf"
                className="p-button-text p-button-rounded pdf-button"
                onClick={handleStudyCertificatePdfButtonClick}
                aria-label="Generate Study Certificate"
                tooltip="הורד אישור לימודים"
                tooltipOptions={{ position: 'bottom' }}
              />
              <Button
                icon={
                  isProgramExpanded(program.id)
                    ? 'pi pi-chevron-up'
                    : 'pi pi-chevron-down'
                }
                className="p-button-text p-button-rounded expand-button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleProgramExpand(program.id);
                }}
                aria-label={
                  isProgramExpanded(program.id) ? 'Collapse' : 'Expand'
                }
              />
            </div>
          </div>
        </div>
        <div className="program-meta-container">
          <div className="program-meta">
            <span className="meta-label">מגמה:</span>
            <span className="meta-value">{program.majorName}</span>
          </div>
          <div className="program-meta">
            <span className="meta-label">שנת התחלה:</span>
            <span className="meta-value">
              {getHebrewYear(program.programStartYear)}{' '}
              {program.programStartYear}
            </span>
          </div>
          <div className="program-meta">
            <span className="meta-label">מזהה שנתי:</span>
            <span className="meta-value">{program.annualSerialNumber}</span>
          </div>
          <div className="program-meta">
            <span className="meta-label">מספר שנים בתוכנית:</span>
            <span className="meta-value">{program.years}</span>
            {/*<span className="meta-value">
              {Math.ceil(
                (new Date().getTime() -
                  new Date(
                    program.studentStatus.studyingStartDate || new Date(),
                  ).getTime()) /
                  (1000 * 60 * 60 * 24 * 365),
              )}
            </span>*/}
          </div>
          <div className="program-meta">
            <span className="meta-label">קורסים:</span>
            <span className="meta-value">{program.courses.length}</span>
          </div>
        </div>

        <ProgramNavigationDialog
          visible={showNavigationDialog}
          programName={program.programName}
          onConfirm={handleNavigateToProgram}
          onCancel={() => setShowNavigationDialog(false)}
        />

        <StudyCertificateProgramNameDialog
          visible={showStudyCertificateNameDialog}
          programName={program.programName}
          additionalProgramName={program.additionalProgramName}
          programStartYear={program.programStartYear}
          programYears={program.years}
          onConfirm={handleStudyCertificateNameDialogConfirm}
          onCancel={() => setShowStudyCertificateNameDialog(false)}
        />
      </div>
    );
  },
);
